/**
 * Assisted primary-provisioning contract (TEAM-UX-PRD §5.2, §8.1). Detect cloud
 * CLIs, preview the exact recipe, launch (Docker / DigitalOcean), and poll
 * status. Launch is owner-or-solo-gated in the route and is NEVER an MCP tool —
 * the execute step is always a human click (agent boundary §8.3).
 */

import { z } from "zod";
import type { Operation } from "../types";

export const provisionTarget = z.enum([
  "docker",
  "digitalocean",
  "flyio",
  "aws",
  "gcp",
  "manual",
]);
export type ProvisionTarget = z.infer<typeof provisionTarget>;

export const provisionerInfoSchema = z.object({
  id: provisionTarget,
  label: z.string(),
  cli: z.string(),
  installed: z.boolean(),
  authenticated: z.boolean().nullable(),
  detail: z.string(),
  supportsExecute: z.boolean(),
});
export type ProvisionerInfo = z.infer<typeof provisionerInfoSchema>;

// coverage-ignore: Hono port deferred after RO-10-09 Next delete; contract retained for apiKey
const detectOp: Operation<void, void, void, { provisioners: ProvisionerInfo[] }> = {
  method: "GET",
  path: () => "/api/provision/detect",
  response: z.object({ provisioners: z.array(provisionerInfoSchema) }),
};

/**
 * A public DNS hostname (e.g. `sync.acme.com`). When supplied, the primary is
 * fronted by Caddy auto-HTTPS and the connect URL is `https://`. Charset-
 * constrained here (and again in recipe.ts's assertDomain) so it can never break
 * out of the generated YAML or the templated `caddy` command line.
 */
const hostname = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/i;

const recipeQuery = z.object({
  target: provisionTarget,
  domain: z.string().max(253).regex(hostname, "domain must be a valid hostname").optional(),
});
const recipeResponse = z.object({
  target: provisionTarget,
  /** Shell command(s) the launch will run, for preview-before-execute. */
  commands: z.array(z.string()),
  /** Generated files (compose / .env / cloud-init), name → contents. */
  files: z.record(z.string(), z.string()),
  /** The libSQL JWT verifying key the primary is configured with. */
  jwtKeyBase64: z.string(),
  /**
   * Estimated cost of running this primary, so the user knows what going
   * multiplayer costs BEFORE they launch (TEAM-UX-PRD "expected costs"). The DB
   * host is the entire added infra cost — there is no per-seat fee, and AI runs
   * on each teammate's own Claude plan.
   */
  cost: z.object({
    monthlyUsd: z.number().nullable(),
    approximate: z.boolean(),
    summary: z.string(),
    billedBy: z.string(),
  }),
});
export type RecipeResponse = z.infer<typeof recipeResponse>;
// coverage-ignore: Hono port deferred after RO-10-09 Next delete; contract retained for apiKey
const recipeOp: Operation<
  void,
  z.infer<typeof recipeQuery>,
  void,
  RecipeResponse
> = {
  method: "GET",
  path: () => "/api/provision/recipe",
  query: recipeQuery,
  response: recipeResponse,
};

/**
 * Cloud region/size slugs across the providers we launch on: DigitalOcean
 * (`nyc1`, `s-1vcpu-1gb`), Fly (`iad`), and AWS Lightsail (`us-east-1`,
 * `nano_3_0` — underscores). Lowercase alphanumeric + hyphen + underscore, and
 * the leading char must be alphanumeric so a value can never reach a CLI argv
 * with a leading `-` (argument injection) — defense-in-depth beyond the length
 * cap, since these flow into `execFile(<cli>, [...])` in src/team/provision.ts.
 */
const doSlug = /^[a-z0-9][a-z0-9_-]*$/;

/**
 * Which sync home base to stand up: the sqld `primary` (solo / my-own-devices,
 * last-write-wins — the default) or the cr-sqlite team `relay` (the real,
 * conflict-free multiplayer home base, WS7). Defaults to `primary` so existing
 * callers are unchanged. Relay launch is supported on the VM targets
 * (docker / digitalocean / aws); other targets stay primary-only for now.
 */
export const provisionService = z.enum(["primary", "relay"]);
export type ProvisionService = z.infer<typeof provisionService>;

const launchBody = z.object({
  target: provisionTarget,
  // Omitted ⇒ the primary path (the route treats undefined as "primary"), so
  // existing callers are unchanged. Optional (not .default) to keep the schema's
  // input and output types identical for the Operation contract.
  service: provisionService.optional(),
  port: z.number().int().min(1).max(65535).optional(),
  region: z.string().max(32).regex(doSlug, "region must be lowercase alphanumeric or hyphen").optional(),
  size: z.string().max(48).regex(doSlug, "size must be lowercase alphanumeric or hyphen").optional(),
  domain: z.string().max(253).regex(hostname, "domain must be a valid hostname").optional(),
});
const launchResponse = z.object({
  ok: z.boolean(),
  logId: z.number(),
  /** Present immediately for Docker; filled async for DigitalOcean. */
  url: z.string().nullable(),
  detail: z.string(),
  /** True when launch runs in the background and the UI should poll status. */
  async: z.boolean(),
});
export type LaunchResponse = z.infer<typeof launchResponse>;
// coverage-ignore: Hono port deferred after RO-10-09 Next delete; contract retained for apiKey
const launchOp: Operation<
  void,
  void,
  z.infer<typeof launchBody>,
  LaunchResponse
> = {
  method: "POST",
  path: () => "/api/provision/launch",
  body: launchBody,
  response: launchResponse,
};

export const provisionLogSchema = z.object({
  id: z.number(),
  target: z.string(),
  status: z.string(),
  host_url: z.string().nullable(),
  detail: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type ProvisionLogRow = z.infer<typeof provisionLogSchema>;
const statusResponse = z.object({
  latest: provisionLogSchema.nullable(),
  log: z.array(provisionLogSchema),
});
// coverage-ignore: Hono port deferred after RO-10-09 Next delete; contract retained for apiKey
const statusOp: Operation<void, void, void, z.infer<typeof statusResponse>> = {
  method: "GET",
  path: () => "/api/provision/status",
  response: statusResponse,
};

export const provision = {
  detect: detectOp,
  recipe: recipeOp,
  launch: launchOp,
  status: statusOp,
};
